import axios from 'axios';
import {
	API_ENDPOINTS,
	BottomAnchoredControls,
	Button,
	ButtonTypes,
	Card,
	CardBody,
	customToast,
	FileHelpers,
	FormError,
	humanFileSize,
	FormFieldDescription,
	ImageType,
	PageLayout,
	replaceKeyWithValue,
	route,
	SingleImageInput,
	StringHelpers,
	SubTitle,
	UploadDataType,
	useDisabledContext,
	useForm,
	useLangContext,
	Variants,
	WidthConstrainedContainer,
	CardPlaceholder,
} from 'carrier-fe';

import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { MAX_FILE_SIZE_10MB } from '../../../Constants';

const defaultData = {
	title: '',
	thumbnail: undefined as ImageType | undefined,
	download_url: '',
};

type Data = typeof defaultData;

function MediaPackForm() {
	const { mediaPackId } = useParams();
	const isEdit = mediaPackId ? true : false;

	const { crud, fields } = useLangContext();
	const { disabled, setDisabled } = useDisabledContext();
	const navigate = useNavigate();

	const [isFetching, setIsFetching] = useState(false);
	const thumbnailChanged = useRef(false);

	const getUploadData = async () => {
		try {
			const response = await axios.get(
				route(API_ENDPOINTS.ADMIN.MEDIA_PACK.UPLOAD_URL)
			);

			return response?.data?.data;
		} catch (err: any) {
			customToast({
				title:
					err?.response?.data?.message ||
					'An error occurred. Please try again.',
				variant: Variants.Danger,
			});
		}
	};

	const updateMediaPack = async (payload: typeof defaultData) => {
		let submitPayload: Partial<typeof defaultData> = { ...payload };

		if (isEdit && !thumbnailChanged.current) {
			delete submitPayload.thumbnail;
		}

		if (submitPayload.thumbnail) {
			delete submitPayload.thumbnail.base64;
		} else if (thumbnailChanged.current) {
			(submitPayload as Record<string, unknown>).thumbnail = null;
		}

		return isEdit
			? axios.patch<{ data: Data; message: string }>(
					route(API_ENDPOINTS.ADMIN.MEDIA_PACK.UPDATE, {
						mediaPack: String(mediaPackId),
					}),
					submitPayload
			  	)
			: axios.post<{ data: Data; message: string }>(
					route(API_ENDPOINTS.ADMIN.MEDIA_PACK.STORE),
					submitPayload
			  	);
	};

	const { Input, Submit, setValues, data, store, errors } = useForm(
		updateMediaPack,
		defaultData
	);

	useEffect(() => {
		if (isEdit) {
			fetchMediaPackData();
		}
	}, []);

	const fetchMediaPackData = async () => {
		if (isFetching) return;

		setIsFetching(true);

		try {
			const { data } = await axios.get<{ data: any }>(
				route(API_ENDPOINTS.ADMIN.MEDIA_PACK.EDIT, {
					mediaPack: String(mediaPackId),
				})
			);

			let thumbnail: ImageType | undefined;
			if (data.data.thumbnail) {
				const t = data.data.thumbnail;
				const extension = t.type
					? t.type.split('/').pop() || 'jpg'
					: 'jpg';

				thumbnail = {
					url: t.url,
					name: t.name || 'Thumbnail',
					extension,
					type: t.type || 'image/jpeg',
					size_bytes: t.size_bytes || 0,
					size_human:
						t.size_bytes > 0
							? humanFileSize(t.size_bytes)
							: 'Unknown',
					created_at: '',
					updated_at: '',
				};
			}

			const formData = {
				title: data.data.title,
				download_url: data.data.download_url,
				thumbnail,
			};

			setValues(formData);
		} catch (err: any) {
			customToast({
				title:
					err?.response?.data?.message ||
					'An error occurred. Please try again.',
				variant: Variants.Danger,
			});
		} finally {
			setIsFetching(false);
		}
	};

	const handleThumbnailChange = async (fileData: ImageType | null) => {
		thumbnailChanged.current = true;

		if (!fileData) {
			setValues({ thumbnail: undefined });
			return;
		}

		setDisabled(true);
		try {
			// Get presigned URL
			const uploadConfig: UploadDataType = await getUploadData();
			if (!uploadConfig) {
				throw new Error('Failed to get upload configuration');
			}

			const response = await FileHelpers.upload(fileData, uploadConfig, MAX_FILE_SIZE_10MB);

			if (response?.ok) {
				setValues({
					thumbnail: {
						...fileData,
						key: uploadConfig.key
					}
				});
			} else {
				throw new Error('Failed to upload image to S3');
			}

		} catch (err: any) {
			customToast({
				title: err.message,
				variant: Variants.Danger,
			});
		} finally {
			setDisabled(false);
		}
	};

	useEffect(() => {
		if (data) {
			navigate('/media-packs');
		}
	}, [data, navigate]);

	let title = replaceKeyWithValue(
		crud?.pages?.create?.title || 'Create :model',
		'model',
		crud?.models?.media_pack || 'Marketing Image Pack'
	);

	if (isEdit) {
		title = replaceKeyWithValue(
			crud?.pages?.edit?.title || 'Edit :model',
			'model',
			crud?.models?.media_pack || 'Marketing Image Pack'
		);
	}

	return (
		<PageLayout title={StringHelpers.title(title)}>
			<WidthConstrainedContainer>
				{isFetching ?
					<CardPlaceholder />
				: 
					<Card>
						<CardBody className={'pb-0'}>

							<SubTitle
								title={StringHelpers.title(
									crud?.sub_titles?.details || 'Details'
								)}
								className="mb-3"
								style={{ color: '#464C5E' }}
							/>

							{Input({
								name: 'title',
								label: fields?.title || 'Title',
							})}

							<div className="mb-4">
								<SingleImageInput
									label={fields?.thumbnail || 'Thumbnail'}
									value={store.thumbnail as ImageType}
									onChange={handleThumbnailChange as any}
									errorField={!!errors?.thumbnail}
									disabled={disabled}
									noMargin
								/>
								<FormFieldDescription
									className={'mt-1 text-end'}
									description={fields?.max_file_size 
										|| 'Max file size: 10MB'
									}
								/>
								{!!errors?.thumbnail && <FormError errorMessages={errors.thumbnail} />}
							</div>							

							{Input({
								name: 'download_url',
								label: fields?.download_url || 'Download URL',
							})}
						</CardBody>
					</Card>
				}
			</WidthConstrainedContainer>
			<BottomAnchoredControls>
				<Button
					label={crud?.buttons?.back?.default || 'Back'}
					onClick={() =>
						isEdit ? navigate(`/media-packs`) : navigate('/media-packs')
					}
					variant={Variants.Dark}
					type={ButtonTypes.Outline}
					className={'me-4'}
					disabled={disabled}
				/>
				<Submit
					defaultLabel={
						isEdit
							? crud?.buttons?.edit?.default || 'Update'
							: crud?.buttons?.create?.default || 'Create'
					}
					loadingLabel={
						isEdit
							? crud?.buttons?.edit?.submitting || 'Updating...'
							: crud?.buttons?.create?.submitting || 'Creating...'
					}
					useOnMouseDown
					disabled={disabled || isFetching}
				/>
			</BottomAnchoredControls>
		</PageLayout>
	);
}

export default MediaPackForm;
